import axios, { AxiosError } from 'axios'
import { clearLocalStorage, TOKEN } from 'utils'
import NotificationSys, { KEEP_UNTIL_DISMISSED } from '../components/NotificationSystem'

const SESSION_IS_EXPIRED = (
  <>
    Your session has expired.
    <br /> Please re-login
  </>
)

const productionBackend = 'https://pams-production-backend-docker.azurewebsites.net'
const stagingBackend = 'https://pams-staging-backend-docker.azurewebsites.net'
const developmentBackend = 'https://pams-development-backend-docker.azurewebsites.net'

export const isDevEnv =
  window.location.hostname !== 'pams-staging.vvtdev.com' &&
  window.location.hostname !== 'memorycarepartner.com'

// Treat staging as non-production for feature flags where needed
export const isStagingEnv = window.location.hostname === 'pams-staging.vvtdev.com'
export const isNonProdEnv = isDevEnv || isStagingEnv
export const isLocalEnv = window.location.hostname === 'localhost'

export const getBaseURL = () => {
  const host = window.location.hostname
  if (host === 'memorycarepartner.com') {
    return productionBackend
  }
  if (host === 'pams-staging.vvtdev.com') {
    return stagingBackend
  }
  if (host === 'pams-development.vvtdev.com') {
    return developmentBackend
  }
  if (host === 'localhost') {
    return process.env.REACT_APP_API_URL
  }
  throw new Error('There is no server API for this host')
}

export const httpHeaders: HeadersInit = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'Access-Control-Allow-Credentials': 'true',
  'Access-Control-Allow-Origin': '*',
  'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
}

const defaultOptions = {
  baseURL: getBaseURL(),
  headers: httpHeaders,
}

const axiosInstance = axios.create(defaultOptions)

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN)
  config.headers['Authorization'] = `Bearer ${token}`
  return config
})

export type IError = AxiosError<{ message?: string; reason?: string }>

type Headers = { get: (value: string) => string | undefined }

axiosInstance.interceptors.response.use(
  (response) => {
    const token = (response?.headers as Headers)?.get('X-Token')
    if (token) {
      localStorage.setItem(TOKEN, token)
    }
    return response
  },
  (error: IError) => {
    const status: number | null = error.response ? error.response.status : null

    // A refused sign-in explains why the account is blocked — the one message the user must not
    // miss, so it stays until they dismiss it or try again.
    const signInAutoDismiss = error.config?.url?.includes('/auth/login')
      ? KEEP_UNTIL_DISMISSED
      : undefined

    if (
      !error.config?.suppressErrorHandler ||
      !error.config?.suppressErrorHandler.includes(error.response?.status || 0)
    ) {
      if (status === 401) {
        clearLocalStorage()
        NotificationSys.showWarning(SESSION_IS_EXPIRED)

        // routers and routes must be imported dynamically
        // otherwise circular reference occurs
        import('../routes/router').then((router) => {
          import('../utils/url').then(({ buildSignInPathWithRedirect }) => {
            router.router.navigate(
              buildSignInPathWithRedirect(
                window.location.pathname,
                window.location.search,
                window.location.hash,
              ),
            )
          })
        })
        return Promise.reject(error)
      } else if (axios.isCancel(error)) {
        NotificationSys.showWarning('Request was canceled')
      } else {
        const axiosError = error as AxiosError<{ message?: string }>
        const data = axiosError.response?.data
        const msg = data?.message
        if (typeof msg === 'string' && msg.trim()) {
          NotificationSys.showWarning(msg, signInAutoDismiss)
        } else {
          NotificationSys.showWarning('Server returned an error', signInAutoDismiss)
        }
      }
    }

    return Promise.reject(error)
  },
)

axios.defaults.baseURL = ''

export default axiosInstance
